"use client";

import { Eye, Play, Radio } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/shared/lib/api";
import type { GoodLuckCoreDTOsTournamentTeamDto as TournamentTeamDto } from "@/shared/api/generated-kubb/types/goodLuck/core/DTOs/TournamentTeamDto";
import { useTournamentStreams } from "@/entities/tournament/api/use-tournament-streams";
import { TwitchEmbed, useTwitchStream } from "@/features/stream";

import { TournamentOfficialStream } from "./tournament-official-stream";
import {
  buildStreamerGroups,
  type LiveStreamer,
  type TeamRosterInput,
} from "./tournament-streamers.logic";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";
import { formatNumberUi, toUiLocale } from "@/shared/lib/ui-locale";

interface TournamentStreamersTabProps {
  tournamentId: string;
  officialChannel?: string | null;
  showOfficialStream?: boolean;
}

/**
 * Вкладка стримеров турнира. Появляется ПОСЛЕ драфта: до него команд ещё нет,
 * группировать не по чему.
 *
 * Кто онлайн — батч-эндпоинт /tournaments/{id}/streams (один запрос, под ним
 * поканальный кэш Twitch). Составы — ПУБЛИЧНЫЙ /tournaments/{id}/teams (draft-
 * эндпоинт под [Authorize] отдал бы анонимной аудитории 401). Джойн по userId;
 * капитан уже входит в members. Пустые команды (никто не в эфире) не показываем —
 * вкладка про «сейчас смотреть».
 */
export const TournamentStreamersTab = ({
  tournamentId,
  officialChannel,
  showOfficialStream,
}: TournamentStreamersTabProps) => {
  const tUi = useTranslations("Ui");
  const { data: streams, isLoading: streamsLoading } =
    useTournamentStreams(tournamentId);

  // Публичный /teams (не /draft/teams — тот под [Authorize], аноним получил бы 401).
  const { data: teamsRaw, isLoading: teamsLoading } = useQuery({
    queryKey: ["tournaments", tournamentId, "teams"],
    queryFn: async () =>
      (
        await api.get<TournamentTeamDto[]>(
          `/tournaments/${encodeURIComponent(tournamentId)}/teams`,
        )
      ).data,
    enabled: Boolean(tournamentId),
    ...QUERY_VOLATILITY.STANDARD,
  });

  const teams: TeamRosterInput[] = useMemo(
    () =>
      (teamsRaw ?? []).map((t) => ({
        id: t.id ?? "",
        name: t.name?.trim() || tUi("litTournamentStreamersTab.komanda"),
        captainId: t.captainId ?? "",
        members: (t.members ?? [])
          .filter((m): m is typeof m & { userId: string } => Boolean(m.userId))
          .map((m) => ({
            userId: m.userId,
            displayName: m.displayName?.trim() || m.userId,
          })),
      })),
    [teamsRaw, tUi],
  );

  const { groups, others } = useMemo(
    () => buildStreamerGroups(streams?.playerStreams ?? [], teams),
    [streams, teams],
  );

  const loading = streamsLoading || teamsLoading;
  const hasOfficial =
    showOfficialStream !== false && Boolean(officialChannel?.trim());
  const nothingLive = groups.length === 0 && others.length === 0;
  // Отсутствие поля = старый бэкенд; читаем как «включено», иначе на не выкаченном
  // сервере вкладка соврала бы «выключено» на каждом турнире.
  const playerStreamersDisabled = streams?.showPlayerStreamers === false;

  return (
    <div className="space-y-6">
      {hasOfficial ? (
        <TournamentOfficialStream channel={officialChannel!.trim()} />
      ) : null}

      {loading && nothingLive ? (
        <div className="py-12 text-center text-muted-foreground">
          {tUi("tournamentStreamersTab.zagruzkaTranslyatsiy")}
        </div>
      ) : nothingLive ? (
        <div className="py-16 text-center">
          <Radio className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          {/* KAN-655: выключенный тумблер и «никто не стримит» — разные вещи,
              а выглядели одинаково: организатор искал причину не там. */}
          {playerStreamersDisabled ? (
            <>
              <h3 className="mb-1 text-lg font-semibold">
                {tUi("tournamentStreamersTab.pokazStrimerovVyklyuchen")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {tUi("tournamentStreamersTab.translyatsiiUchastnikovNePokazyvayutsya")}
              </p>
            </>
          ) : (
            <>
              <h3 className="mb-1 text-lg font-semibold">
                {tUi("tournamentStreamersTab.seychasNiktoNeStrimit")}
              </h3>
              <p className="text-sm text-muted-foreground">
                {tUi("tournamentStreamersTab.zdesPoyavyatsyaUchastnikiKotorye")}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.teamId} className="space-y-3">
              <h3 className="text-lg font-bold">{g.teamName}</h3>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {g.streamers.map((s) => (
                  <StreamerCard key={s.userId} streamer={s} />
                ))}
              </div>
            </section>
          ))}

          {others.length > 0 ? (
            <section className="space-y-3">
              <h3 className="text-lg font-bold">{tUi("tournamentStreamersTab.vneKomand")}</h3>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {others.map((s) => (
                  <StreamerCard key={s.userId} streamer={s} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
};

const STREAM_REFETCH_INTERVAL_MS = 75_000;

/**
 * Карточка одного стримера: превью-кадр (клик → встроенный плеер).
 *
 * Видимостью карточки управляет БАТЧ (он уже подтвердил «онлайн» и питает
 * группировку) — сама карточка не решает, показываться ли ей, иначе при загрузке
 * per-card запроса мигал бы пустой заголовок команды. useTwitchStream здесь только
 * обогащает: живое превью + счётчик зрителей. Превью с фолбэком на статичный кадр
 * Twitch (static-cdn.jtvnw.net, разрешён в CSP) — работает и без метаданных.
 * Ушедших в офлайн уберёт следующий тик батча.
 */
const StreamerCard = ({ streamer }: { streamer: LiveStreamer }) => {
  const tUi = useTranslations("Ui");
  const locale = toUiLocale(useLocale());
  const [playing, setPlaying] = useState(false);
  const { stream } = useTwitchStream(streamer.twitchLogin, {
    refetchIntervalMs: STREAM_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  const viewers = stream?.viewer_count;
  const title = stream?.title?.trim();
  const thumb =
    stream?.thumbnail_url?.replace("{width}", "640").replace("{height}", "360") ||
    `https://static-cdn.jtvnw.net/previews-ttv/live_user_${streamer.twitchLogin}-640x360.jpg`;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="relative aspect-video w-full bg-black">
        {playing ? (
          <TwitchEmbed channel={streamer.twitchLogin} muted />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group relative block h-full w-full"
            aria-label={tUi("litTournamentStreamersTab.smotretTranslyatsiyu", { name: streamer.displayName })}
          >
            <img
              src={thumb}
              alt=""
              className="h-full w-full bg-muted object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/60 backdrop-blur-sm transition-transform group-hover:scale-110">
                <Play className="h-6 w-6 translate-x-0.5 text-white" fill="currentColor" />
              </span>
            </span>
            <span className="absolute left-2 top-2 inline-flex items-center gap-1.5 rounded-md bg-red-600 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-white">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
              {tUi("tournamentStreamersTab.vEfire")}
            </span>
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 p-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-semibold">
              {streamer.displayName}
            </span>
            {streamer.isCaptain ? (
              <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-500">
                {tUi("tournamentStreamersTab.kapitan")}
              </span>
            ) : null}
          </div>
          <a
            href={`https://www.twitch.tv/${streamer.twitchLogin}`}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-xs text-muted-foreground hover:text-foreground"
            title={title}
          >
            twitch.tv/{streamer.twitchLogin}
          </a>
        </div>
        {viewers !== undefined ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-md bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-400">
            <Eye className="h-3 w-3" />
            {formatNumberUi(viewers, undefined, locale)}
          </span>
        ) : null}
      </div>
    </div>
  );
};
